/**
 * Public Resume Page
 *
 * Renders a traditional resume from canonical data.
 * Designed for print: browser Print → PDF covers export.
 * Clean semantic HTML for ATS compatibility.
 */

import { useEffect, useState } from 'preact/hooks';

const API_BASE = import.meta.env.VITE_API_URL || 'https://pxtxt.itsmin.workers.dev';

interface ResumeData {
	resumeFormat: 'ats' | 'narrative';
	header: {
		name?: string;
		title?: string;
		email?: string;
		linkedin?: string;
		portfolio?: string;
	};
	summary?: string;
	experience: Array<{
		company: string;
		title?: string;
		period?: string;
		description?: string;
		highlights: string[];
	}>;
	skills: {
		capabilities: string[];
		tools: string[];
		domains: string[];
	};
	projects: Array<{
		name: string;
		company?: string;
		summary?: string;
	}>;
	narrativeReadiness?: {
		rolesWithNarratives: number;
		totalRoles: number;
	};
}

interface Props {
	slug: string;
}

export function PublicResume({ slug }: Props) {
	const [resume, setResume] = useState<ResumeData | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	useEffect(() => {
		loadResume();
	}, [slug]);

	async function loadResume() {
		try {
			setLoading(true);
			const res = await fetch(`${API_BASE}/api/public/resume/${slug}`);
			if (!res.ok) {
				setError(res.status === 404 ? 'Résumé not found' : 'Failed to load résumé');
				return;
			}
			setResume(await res.json());
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to load résumé');
		} finally {
			setLoading(false);
		}
	}


	if (loading) {
		return (
			<div class="resume-page">
				<div class="resume-loading">Loading résumé...</div>
				<style>{resumeStyles}</style>
			</div>
		);
	}

	if (error || !resume) {
		return (
			<div class="resume-page">
				<div class="resume-error">
					<h2>Résumé Not Available</h2>
					<p>{error || 'This résumé is not available.'}</p>
					<a href={`/b/${slug}`}>View Profile</a>
				</div>
				<style>{resumeStyles}</style>
			</div>
		);
	}

	const { header, summary, experience, skills, projects } = resume;
	const hasSkills = skills.capabilities.length > 0 || skills.tools.length > 0 || skills.domains.length > 0;

	return (
		<div class="resume-page">
			{/* Print toolbar — hidden in print */}
			<div class="resume-toolbar no-print">
				<a href={`/b/${slug}`} class="toolbar-link">View Profile</a>
				<div class="toolbar-actions">
					<button class="toolbar-btn" onClick={() => window.print()}>
						Print / Save PDF
					</button>
				</div>
			</div>

			<article class="resume">
				{/* ── Header ── */}
				<header class="resume-header">
					{header.name && <h1 class="resume-name">{header.name}</h1>}
					{header.title && <p class="resume-title">{header.title}</p>}
					<div class="resume-contact">
						{header.email && <a href={`mailto:${header.email}`}>{header.email}</a>}
						{header.linkedin && (
							<a href={header.linkedin} target="_blank" rel="noopener noreferrer">
								{header.linkedin.replace(/^https?:\/\/(www\.)?/, '')}
							</a>
						)}
						{header.portfolio && (
							<a href={header.portfolio} target="_blank" rel="noopener noreferrer">
								{header.portfolio.replace(/^https?:\/\/(www\.)?/, '')}
							</a>
						)}
					</div>
				</header>

				{/* ── Summary ── */}
				{summary && (
					<section class="resume-section">
						<p class="resume-summary">{summary}</p>
					</section>
				)}

				{/* ── Experience ── */}
				{experience.length > 0 && (
					<section class="resume-section">
						<h2 class="resume-section-title">Experience</h2>
						{experience.map((job, i) => (
							<div key={i} class="resume-job">
								<div class="resume-job-header">
									<div class="resume-job-left">
										<span class="resume-company">{job.company}</span>
										{job.title && <span class="resume-role">{job.title}</span>}
									</div>
									{job.period && <span class="resume-period">{job.period}</span>}
								</div>
								{job.description && (
									<p class="resume-description">{job.description}</p>
								)}
								{job.highlights.length > 0 && (
									<ul class="resume-highlights">
										{job.highlights.map((h, j) => (
											<li key={j}>{h}</li>
										))}
									</ul>
								)}
							</div>
						))}
					</section>
				)}

				{/* ── Skills ── */}
				{hasSkills && (
					<section class="resume-section">
						<h2 class="resume-section-title">Skills</h2>
						<div class="resume-skills">
							{skills.domains.length > 0 && (
								<div class="resume-skill-row">
									<span class="resume-skill-label">Domains</span>
									<span>{skills.domains.join(', ')}</span>
								</div>
							)}
							{skills.capabilities.length > 0 && (
								<div class="resume-skill-row">
									<span class="resume-skill-label">Capabilities</span>
									<span>{skills.capabilities.join(', ')}</span>
								</div>
							)}
							{skills.tools.length > 0 && (
								<div class="resume-skill-row">
									<span class="resume-skill-label">Tools</span>
									<span>{skills.tools.join(', ')}</span>
								</div>
							)}
						</div>
					</section>
				)}

				{/* ── Projects ── */}
				{projects.length > 0 && (
					<section class="resume-section">
						<h2 class="resume-section-title">Projects</h2>
						{projects.map((p, i) => (
							<div key={i} class="resume-project">
								<span class="resume-project-name">{p.name}</span>
								{p.company && <span class="resume-project-company"> — {p.company}</span>}
								{p.summary && <p class="resume-project-summary">{p.summary}</p>}
							</div>
						))}
					</section>
				)}
			</article>

			<style>{resumeStyles}</style>
		</div>
	);
}

const resumeStyles = `
	/* ── Page ── */

	.resume-page {
		min-height: 100vh;
		background: var(--color-bg);
	}

	.resume-loading,
	.resume-error {
		text-align: center;
		padding: 4rem 2rem;
		font-family: var(--font-body);
		color: var(--color-text-muted);
	}

	.resume-error h2 {
		font-family: var(--font-display);
		font-weight: 300;
		color: var(--color-text);
		margin-bottom: 0.5rem;
	}

	.resume-error a {
		color: var(--color-primary);
		text-decoration: none;
		margin-top: 1rem;
		display: inline-block;
	}

	/* ── Toolbar ── */

	.resume-toolbar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		max-width: 800px;
		margin: 0 auto;
		padding: 1rem 2rem;
	}

	.toolbar-link {
		font-family: var(--font-mono);
		font-size: 0.75rem;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		color: var(--color-text-muted);
		text-decoration: none;
	}

	.toolbar-link:hover {
		color: var(--color-primary);
	}

	.toolbar-btn {
		font-family: var(--font-mono);
		font-size: 0.75rem;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		color: var(--color-text-muted);
		background: none;
		border: 1px solid var(--color-border);
		padding: 0.5rem 1rem;
		cursor: pointer;
		transition: color 150ms, border-color 150ms;
	}

	.toolbar-btn:hover {
		color: var(--color-primary);
		border-color: var(--color-primary);
	}

	.toolbar-actions {
		display: flex;
		align-items: center;
		gap: 0.75rem;
	}

	.format-toggle {
		display: flex;
		border: 1px solid var(--color-border);
		overflow: hidden;
	}

	.toggle-btn {
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		padding: 0.375rem 0.75rem;
		background: none;
		border: none;
		border-right: 1px solid var(--color-border);
		color: var(--color-text-muted);
		cursor: pointer;
		transition: color 150ms, background-color 150ms;
	}

	.toggle-btn:last-child {
		border-right: none;
	}

	.toggle-btn:hover {
		color: var(--color-primary);
	}

	.toggle-btn.active {
		background: var(--color-primary-muted);
		color: var(--color-primary);
	}

	.resume-narrative-notice {
		justify-content: center;
		font-size: 0.8125rem;
		color: var(--color-text-muted);
		padding: 0.5rem 2rem;
		background: rgba(245, 158, 11, 0.06);
		border-bottom: 1px solid rgba(245, 158, 11, 0.15);
	}

	/* ── Resume ── */

	.resume {
		max-width: 800px;
		margin: 0 auto;
		padding: 3rem 2.5rem;
		background: var(--color-bg-elevated);
		border: 1px solid var(--color-border);
		font-family: var(--font-body);
		color: var(--color-text);
	}

	/* ── Header ── */

	.resume-header {
		margin-bottom: 1.5rem;
		padding-bottom: 1.5rem;
		border-bottom: 2px solid var(--color-text);
	}

	.resume-name {
		font-family: var(--font-display);
		font-size: 1.75rem;
		font-weight: 400;
		margin: 0;
		letter-spacing: 0.01em;
	}

	.resume-title {
		font-size: 1rem;
		color: var(--color-text-muted);
		margin: 0.25rem 0 0;
	}

	.resume-contact {
		display: flex;
		flex-wrap: wrap;
		gap: 1rem;
		margin-top: 0.75rem;
		font-size: 0.8125rem;
	}

	.resume-contact a {
		color: var(--color-text-muted);
		text-decoration: none;
	}

	.resume-contact a:hover {
		color: var(--color-primary);
	}

	/* ── Sections ── */

	.resume-section {
		margin-bottom: 1.5rem;
	}

	.resume-section:last-child {
		margin-bottom: 0;
	}

	.resume-section-title {
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		font-weight: 400;
		text-transform: uppercase;
		letter-spacing: 0.1em;
		color: var(--color-text-subtle);
		margin: 0 0 0.75rem;
		padding-bottom: 0.375rem;
		border-bottom: 1px solid var(--color-border);
	}

	.resume-summary {
		font-size: 0.9375rem;
		line-height: 1.5;
		color: var(--color-text-muted);
		margin: 0;
	}

	/* ── Experience ── */

	.resume-job {
		margin-bottom: 1.25rem;
	}

	.resume-job:last-child {
		margin-bottom: 0;
	}

	.resume-job-header {
		display: flex;
		justify-content: space-between;
		align-items: baseline;
		flex-wrap: wrap;
		gap: 0.5rem;
		margin-bottom: 0.375rem;
	}

	.resume-job-left {
		display: flex;
		flex-direction: column;
	}

	.resume-company {
		font-weight: 600;
		font-size: 0.9375rem;
	}

	.resume-role {
		font-size: 0.875rem;
		color: var(--color-text-muted);
	}

	.resume-period {
		font-size: 0.8125rem;
		color: var(--color-text-subtle);
		white-space: nowrap;
	}

	.resume-description {
		font-size: 0.875rem;
		line-height: 1.6;
		color: var(--color-text);
		margin: 0.25rem 0 0.375rem;
	}

	.resume-highlights {
		margin: 0;
		padding-left: 1.25rem;
		list-style: disc;
	}

	.resume-highlights li {
		font-size: 0.875rem;
		line-height: 1.5;
		margin-bottom: 0.125rem;
		color: var(--color-text);
	}

	/* ── Skills ── */

	.resume-skills {
		display: flex;
		flex-direction: column;
		gap: 0.25rem;
	}

	.resume-skill-row {
		display: flex;
		gap: 0.75rem;
		font-size: 0.875rem;
		line-height: 1.5;
	}

	.resume-skill-label {
		flex-shrink: 0;
		width: 6.5rem;
		font-weight: 500;
		color: var(--color-text-muted);
	}

	/* ── Projects ── */

	.resume-project {
		margin-bottom: 0.5rem;
	}

	.resume-project:last-child {
		margin-bottom: 0;
	}

	.resume-project-name {
		font-weight: 600;
		font-size: 0.875rem;
	}

	.resume-project-company {
		font-size: 0.875rem;
		color: var(--color-text-muted);
	}

	.resume-project-summary {
		font-size: 0.8125rem;
		color: var(--color-text-muted);
		margin: 0.125rem 0 0;
		line-height: 1.5;
	}

	/* ── Print ── */

	@media print {
		.no-print {
			display: none !important;
		}

		.resume-page {
			background: white;
			min-height: auto;
		}

		.resume {
			border: none;
			padding: 0;
			margin: 0;
			max-width: none;
			box-shadow: none;
			background: white;
			color: black;
		}

		.resume-header {
			border-bottom-color: black;
		}

		.resume-name {
			color: black;
		}

		.resume-title,
		.resume-contact a,
		.resume-role,
		.resume-period,
		.resume-summary,
		.resume-skill-label,
		.resume-project-company,
		.resume-project-summary {
			color: #444;
		}

		.resume-section-title {
			color: #666;
			border-bottom-color: #ccc;
		}

		.resume-description {
			color: black;
		}

		.resume-highlights li {
			color: black;
		}

		.resume-job {
			page-break-inside: avoid;
		}

		.resume-project {
			page-break-inside: avoid;
		}

		@page {
			margin: 0.75in;
		}
	}

	/* ── Responsive ── */

	@media (max-width: 640px) {
		.resume {
			padding: 2rem 1.5rem;
		}

		.resume-toolbar {
			padding: 1rem 1.5rem;
		}

		.resume-narrative-notice {
			padding: 0.5rem 1.5rem;
			font-size: 0.75rem;
		}

		.resume-job-header {
			flex-direction: column;
			gap: 0;
		}

		.resume-skill-row {
			flex-direction: column;
			gap: 0;
			word-break: break-word;
		}

		.resume-skill-label {
			width: auto;
		}
	}
`;
